// SEQ-180 phase 2, P24 / D26: the project P&L tab's operational band. One
// row per operational metric the user may type into, with monthly cells
// anyone who can edit the dollar rows can edit.
//
// 17 SEPTEMBER 2026: THIS IS NOW THE WHOLE BAND. It used to be the editable
// half, sitting under PnlOperationalRows, which rendered Ordinary Hours,
// Overtime Hours and Total Labour Hours read-only from payroll.
// 20260917000100_pnl_manual_labour_hours.sql ended that: hours are typed by
// hand, so the two hours keys arrive through this component's own query
// (manualEntryRegistryFilter.ts) and the read-only component was removed
// rather than left rendering the same two numbers a second time. Total
// Labour Hours is derived here from the two rows above it, so it cannot
// disagree with a cell the user just edited.
//
// THE INVARIANT: nothing here can move a dollar total. These metrics are
// non-currency registry rows with financial_inclusion = false, which the
// schema CHECK enforces (F23), and their values live in their own table,
// never in claims_pnl_monthly_values and never in
// app_settings.pnl_expense_categories. The Wages section total above is
// computed from `categoriesByGroup` alone and does not see this component.

import { useState } from "react";
import { createPortal } from "react-dom";
import { Edit3 } from "lucide-react";
import { formatMetricValue } from "../../lib/pnl/formatMetricValue";
import { InfoTooltip } from "../InfoTooltip";
import { PnlEditCellModal } from "./PnlEditCellModal";
import { PNL_METRIC_KEYS } from "../../lib/pnl/pnlAnalytics";
import { useProjectManualMetrics } from "../../features/pnlAnalytics/useProjectManualMetrics";
import type { ManualOperationalMetric } from "../../features/pnlAnalytics/useProjectManualMetrics";
import {
  countMonthsWithValue,
  totalManualMetric,
} from "../../features/pnlAnalytics/manualMetricTotals";
import { labourHoursTotal } from "../../features/pnlAnalytics/operationalHoursTotals";
import {
  useProjectOperationalHours,
  periodMonthToColumnKey,
} from "../../features/pnlAnalytics/useProjectOperationalHours";
import type { OperationalHoursMonthColumn } from "../../features/pnlAnalytics/useProjectOperationalHours";
import { useToast } from "../ui/ToastModal";

const MANUAL_TOOLTIP_TEXT =
  "Entered by hand, month by month. Not part of any P&L dollar total. Clear a cell to remove the value for that month.";

// Worded separately from MANUAL_TOOLTIP_TEXT because the question these two
// rows raise is a different one: what happens to the payroll figure.
// Between 17 and 18 September 2026 these rows really were typed and only
// typed, and this tooltip said so. SEQ-185 put payroll back and demoted
// typing to an override, so the old wording became false on every mapped
// project. What it has to answer now is what a typed value does to the
// payroll figure, and what clearing it gives back.
const HOURS_TOOLTIP_TEXT =
  "Hours come from payroll. Type a value to override payroll for that month. Clear the cell to go back to the payroll figure.";

// Derived, never typed and never fetched: pnl_metric_registry defines
// calculated.total_labour_hours as ordinary hours plus overtime hours, and
// this row is that same sum, taken from the two rows directly above it.
const TOTAL_LABOUR_HOURS_LABEL = "Total Labour Hours";
const TOTAL_LABOUR_HOURS_TOOLTIP =
  "Ordinary hours plus overtime hours, added up for you. Nothing to type here.";

interface PnlManualMetricRowsProps {
  projectId: string;
  monthColumns: OperationalHoursMonthColumn[];
  colLabelWidth: number;
  colTotalWidth: number;
  colMonthWidth: number;
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Aug 2026", for a cell's accessible name. The visible header already carries the month; this is for a screen reader reading one cell out of context. */
function monthCellLabel(column: OperationalHoursMonthColumn): string {
  return `${MONTH_NAMES[column.month - 1] ?? column.month} ${column.year}`;
}

function cellStyle(width: number, sticky?: { left: number }) {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    padding: "8px 12px",
    textAlign: "right" as const,
    fontSize: 12.5,
    whiteSpace: "nowrap" as const,
    ...(sticky ? { position: "sticky" as const, left: sticky.left } : {}),
  };
}

interface EditTarget {
  metric: ManualOperationalMetric;
  columnKey: string;
  month: number;
  year: number;
  value: number | null;
}

export function PnlManualMetricRows({
  projectId,
  monthColumns,
  colLabelWidth,
  colTotalWidth,
  colMonthWidth,
}: PnlManualMetricRowsProps) {
  const { metrics, valuesByMetric, loading, error, saveValue } = useProjectManualMetrics(
    projectId,
    monthColumns,
  );
  // SEQ-185: the payroll figure behind the two hours rows. Re-mounted after
  // 20260917000100 unmounted it, because 20260918000100 made payroll the
  // source again and a typed value only an override. Without this the tab
  // shows a blank cell on every month nobody has typed, while the dashboard,
  // which reads the same RPC, shows the payroll number. Two screens
  // disagreeing about hours is the complaint this whole series started from.
  //
  // Deliberately NOT blocking: if this is still loading or the RPC fails, the
  // band renders the typed values alone rather than nothing. The hook's own
  // header asks for exactly that.
  const payrollHours = useProjectOperationalHours(projectId, monthColumns);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  if (loading) return null;

  if (error) {
    return (
      <tr className="pnl-operational-row pnl-operational-row-status">
        <td className="pnl-cat-label pnl-sticky-label" style={cellStyle(colLabelWidth)}>
          Operational metrics
          <InfoTooltip text="Your organisation's operational metrics could not be loaded. The rest of the P&L is unaffected." />
        </td>
        <td className="pnl-sticky-total" style={cellStyle(colTotalWidth, { left: colLabelWidth })}>
          <span className="pnl-operational-placeholder">Unavailable</span>
        </td>
        <td colSpan={monthColumns.length} style={{ padding: "8px 12px" }}>
          <span className="pnl-operational-placeholder">Unavailable</span>
        </td>
      </tr>
    );
  }

  // Nothing to render at all. Since 17 September 2026 the two canonical
  // hours keys are in this list on every project, so in practice this is a
  // registry from before that migration, which the error branch above has
  // usually caught first.
  if (metrics.length === 0) return null;

  async function handleSave(value: number | null) {
    if (!editTarget) return;
    setSaving(true);
    const message = await saveValue(editTarget.metric.metricKey, editTarget.columnKey, value);
    setSaving(false);
    if (message) {
      addToast({ type: "error", title: "Value not saved", message });
      return;
    }
    setEditTarget(null);
  }

  // Resolved once, because the derived Total Labour Hours row below folds
  // the same per-month values as the two rows it sits under.
  // Payroll, keyed the way the tab's own columns are keyed. Only the two
  // canonical hours keys have a payroll source; an organisation's own custom
  // metric has none, so it keeps its typed-or-blank behaviour untouched.
  const payrollByMetric = new Map<string, Map<string, number | null>>();
  if (payrollHours.months) {
    const ord = new Map<string, number | null>();
    const ot = new Map<string, number | null>();
    for (const m of payrollHours.months) {
      const key = periodMonthToColumnKey(m.periodMonth);
      ord.set(key, m.ordinaryHours);
      ot.set(key, m.overtimeHours);
    }
    payrollByMetric.set(PNL_METRIC_KEYS.labour.ordinaryHours, ord);
    payrollByMetric.set(PNL_METRIC_KEYS.labour.overtimeHours, ot);
  }

  const rows = metrics.map((metric) => {
    const byMonth = valuesByMetric.get(metric.metricKey);
    const fromPayroll = payrollByMetric.get(metric.metricKey);
    // typedValues is what the user actually owns; monthValues is what the cell
    // shows. They differ exactly where payroll fills a month nobody typed.
    const typedValues = monthColumns.map((mc) => byMonth?.get(mc.key) ?? null);
    const monthValues = monthColumns.map((mc, i) =>
      typedValues[i] !== null ? typedValues[i] : (fromPayroll?.get(mc.key) ?? null),
    );
    return {
      metric,
      typedValues,
      monthValues,
      total: totalManualMetric(monthValues, metric.aggregation),
      filledMonths: countMonthsWithValue(monthValues),
    };
  });

  const ordinaryRow = rows.find((r) => r.metric.metricKey === PNL_METRIC_KEYS.labour.ordinaryHours);
  const overtimeRow = rows.find((r) => r.metric.metricKey === PNL_METRIC_KEYS.labour.overtimeHours);
  /** The row the subtotal follows. Undefined when the registry carries no manual system key at all. */
  const lastSystemKey = rows.filter((r) => r.metric.isSystemManualInput).at(-1)?.metric.metricKey;

  /**
   * Total Labour Hours, derived here rather than read back from
   * calculated.total_labour_hours through the RPC, so it cannot disagree
   * with the two rows above it while a just-typed cell is still on screen.
   *
   * No pnl-manual-metric-row class, deliberately: that class is what makes a
   * label upright and primary coloured, and its absence leaves this one
   * italic and secondary, which is how the band already says a row is not
   * yours to type in.
   *
   * Both hours rows or nothing. A subtotal of a single row says nothing, and
   * it would appear on a registry halfway through the migration.
   */
  function renderLabourHoursTotal() {
    if (!ordinaryRow || !overtimeRow) return null;
    const decimals = ordinaryRow.metric.decimals;
    const total = labourHoursTotal(ordinaryRow.total, overtimeRow.total);
    return (
      <tr key={PNL_METRIC_KEYS.calculated.totalLabourHours} className="pnl-cat-row pnl-operational-row">
        <td
          className="pnl-cat-label pnl-sticky-label"
          style={{ ...cellStyle(colLabelWidth), textAlign: "left", fontWeight: 700 }}
        >
          {TOTAL_LABOUR_HOURS_LABEL}
          <InfoTooltip text={TOTAL_LABOUR_HOURS_TOOLTIP} />
        </td>
        <td
          className="pnl-sticky-total"
          style={{ ...cellStyle(colTotalWidth, { left: colLabelWidth }), fontWeight: 700 }}
          data-seq-private
        >
          {total === null ? (
            <span className="pnl-operational-placeholder">No values</span>
          ) : (
            formatMetricValue(total, "hours", decimals)
          )}
        </td>
        {monthColumns.map((mc, index) => (
          <td key={mc.key} style={cellStyle(colMonthWidth)} data-seq-private>
            {formatMetricValue(
              labourHoursTotal(ordinaryRow.monthValues[index], overtimeRow.monthValues[index]),
              "hours",
              decimals,
              { nullPlaceholder: "" },
            )}
          </td>
        ))}
      </tr>
    );
  }

  return (
    <>
      {/* flatMap, not map: Total Labour Hours belongs directly under the two
          rows it adds up, which is where it sat while payroll supplied it,
          and the organisation's own metrics follow below it. */}
      {rows.flatMap(({ metric, typedValues, monthValues, total, filledMonths }) => {
        const row = (
          <tr
            key={metric.metricKey}
            className="pnl-cat-row pnl-operational-row pnl-manual-metric-row"
          >
            <td
              className="pnl-cat-label pnl-sticky-label"
              style={{ ...cellStyle(colLabelWidth), textAlign: "left", fontWeight: 500 }}
            >
              {metric.displayName}
              <InfoTooltip text={metric.isSystemManualInput ? HOURS_TOOLTIP_TEXT : MANUAL_TOOLTIP_TEXT} />
            </td>
            <td
              className="pnl-sticky-total"
              style={{ ...cellStyle(colTotalWidth, { left: colLabelWidth }), fontWeight: 600 }}
              data-seq-private
              title={
                total === null
                  ? undefined
                  : metric.aggregation === "avg"
                    ? `Average of ${filledMonths} ${filledMonths === 1 ? "month" : "months"} with a value`
                    : `Total of ${filledMonths} ${filledMonths === 1 ? "month" : "months"} with a value`
              }
            >
              {total === null ? (
                <span className="pnl-operational-placeholder">No values</span>
              ) : (
                formatMetricValue(total, metric.unit, metric.decimals)
              )}
            </td>
            {monthColumns.map((mc, index) => {
              const value = monthValues[index];
              // Shown from payroll because nobody typed this month. The editor
              // still opens on the TYPED value, which is null here, so the
              // field starts empty: saving the payroll number back unchanged
              // would create an override that then stops following payroll.
              const isFromPayroll = typedValues[index] === null && value !== null;
              const openEditor = () =>
                setEditTarget({
                  metric,
                  columnKey: mc.key,
                  month: mc.month,
                  year: mc.year,
                  value: typedValues[index],
                });
              return (
                <td
                  key={mc.key}
                  className="pnl-edit-cell"
                  style={cellStyle(colMonthWidth)}
                  // React review, MEDIUM: an editable cell that only responds
                  // to a click cannot be opened by a keyboard user at all.
                  // The same three attributes are on the dollar cells in
                  // ProjectStatusTab.tsx, so the two editable bands behave
                  // the same way rather than one being reachable and the
                  // other not.
                  role="button"
                  tabIndex={0}
                  aria-label={
                    isFromPayroll
                      ? `Edit ${metric.displayName} for ${monthCellLabel(mc)}, currently ${formatMetricValue(value, metric.unit, metric.decimals)} from payroll`
                      : `Edit ${metric.displayName} for ${monthCellLabel(mc)}`
                  }
                  title={isFromPayroll ? "From payroll. Type a value here to override it." : undefined}
                  onClick={openEditor}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openEditor();
                    }
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 4,
                    }}
                  >
                    <span style={isFromPayroll ? { opacity: 0.68 } : undefined} data-seq-private>
                      {formatMetricValue(value, metric.unit, metric.decimals, {
                        nullPlaceholder: "",
                      })}
                    </span>
                    <Edit3
                      size={10}
                      className="pnl-edit-icon"
                      style={{ flexShrink: 0, color: "var(--color-primary)" }}
                    />
                  </div>
                </td>
              );
            })}
          </tr>
        );
        return metric.metricKey === lastSystemKey ? [row, renderLabourHoursTotal()] : [row];
      })}

      {/* Portalled to the body: a <tr> parent cannot legally contain the
          editor's <div>, and the dollar-cell editor is rendered outside the
          table for the same reason. */}
      {editTarget && createPortal(
        <PnlEditCellModal
          key={`manual-${editTarget.metric.metricKey}-${editTarget.columnKey}`}
          isOpen
          onClose={() => setEditTarget(null)}
          onSave={handleSave}
          categoryName={editTarget.metric.displayName}
          month={editTarget.month}
          year={editTarget.year}
          currentValue={editTarget.value}
          saving={saving}
          unit={editTarget.metric.unit}
          allowClear
          hint={
            editTarget.metric.aggregation === "avg"
              ? "The Total column averages the months that carry a value. Leave this blank to remove the month from that average."
              : "The Total column adds up the months that carry a value. Leave this blank to remove the month from that total."
          }
        />,
        document.body,
      )}
    </>
  );
}
